import { Building2, Contact, DollarSignIcon, FilePlus2, Globe, Network } from 'lucide-react';

import type { AgreementFormSchema, AgreementQuerySchema } from '@/_pages/agreements/shared/schemas';
import type { CompanyFormSchema, CompanyQuerySchema } from '@/_pages/companies/shared/schemas';
import type { CompetitorFormSchema, CompetitorQuerySchema } from '@/_pages/competitors/shared/schemas';
import type { ContactFormSchema, ContactQuerySchema } from '@/_pages/contacts/shared/schemas';
import type { DomainFormSchema, DomainQuerySchema } from '@/_pages/domains/shared/schemas';
import type {
  InventoryVendorFormSchema,
  InventoryVendorQuerySchema,
} from '@/_pages/settings/inventory-vendors/shared/schemas';
import type { ManufacturerFormSchema, ManufacturerQuerySchema } from '@/_pages/settings/manufacturers/shared';
import type { TeamMemberFormSchema, TeamMemberQuerySchema } from '@/_pages/team-members/shared/schemas';
import Competitors from '@/_shared/images/icons/competitors.svg?react';
import type { RouteListItem, ToPath } from '@/types';

export type QuickActionPaths = Extract<
  ToPath,
  | '/account-receivables/new'
  | '/team-members/new'
  | '/companies/new'
  | '/contacts/new'
  | '/competitors/new'
  | '/domains/new'
  | '/settings/manufacturers/new'
  | '/settings/inventory-vendors/new'
  | '/agreements/new'
>;

export const quickActions: (RouteListItem & { path: QuickActionPaths; showInMenu: boolean; model_name: string })[] = [
  {
    path: '/account-receivables/new',
    name: 'AR Payment',
    icon: DollarSignIcon,
    showInMenu: true,
    model_name: 'account-receivables',
  },
  { path: '/team-members/new', name: 'New Team Member', icon: Network, showInMenu: true, model_name: 'team_member' },
  { path: '/companies/new', name: 'New Company', icon: Building2, showInMenu: true, model_name: 'client' },
  { path: '/contacts/new', name: 'New Contact', icon: Contact, showInMenu: true, model_name: 'contact' },
  { path: '/competitors/new', name: 'New Competitor', icon: Competitors, showInMenu: true, model_name: 'competitor' },
  { path: '/domains/new', name: 'New Domain', icon: Globe, showInMenu: true, model_name: 'domain' },
  { path: '/agreements/new', name: 'New Agreement', icon: FilePlus2, showInMenu: true, model_name: 'agreement' },
  {
    path: '/settings/manufacturers/new',
    name: 'New Manufacturer',
    icon: Globe,
    showInMenu: false,
    model_name: 'manufacturers',
  },
] as const;

export type QuickActionResponseType = {
  '/account-receivables/new': TeamMemberQuerySchema;
  '/team-members/new': TeamMemberQuerySchema;
  '/companies/new': CompanyQuerySchema;
  '/contacts/new': ContactQuerySchema;
  '/competitors/new': CompetitorQuerySchema;
  '/domains/new': DomainQuerySchema;
  '/settings/manufacturers/new': ManufacturerQuerySchema;
  '/settings/inventory-vendors/new': InventoryVendorQuerySchema;
  '/agreements/new': AgreementQuerySchema;
};

export type QuickActionMetaFormeType = {
  '/account-receivables/new': TeamMemberQuerySchema;
  '/team-members/new': TeamMemberFormSchema;
  '/companies/new': CompanyFormSchema;
  '/contacts/new': ContactFormSchema;
  '/competitors/new': CompetitorFormSchema;
  '/domains/new': DomainFormSchema;
  '/settings/manufacturers/new': ManufacturerFormSchema;
  '/settings/inventory-vendors/new': InventoryVendorFormSchema;
  '/agreements/new': AgreementFormSchema;
};

export type QuickAddQuerySchema =
  | TeamMemberQuerySchema
  | CompanyQuerySchema
  | ContactQuerySchema
  | CompetitorQuerySchema
  | DomainQuerySchema
  | ManufacturerQuerySchema
  | InventoryVendorQuerySchema
  | AgreementQuerySchema;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type QuickAddMenuContextType<T extends keyof QuickActionResponseType = any> = {
  activeAction: T | null;
  metadata: Partial<QuickActionMetaFormeType[T]> | null;
  showModal: (
    action: T,
    triggerChangeCallback?: (item: QuickActionResponseType[T]) => Promise<void> | void,
    metadata?: Partial<QuickActionResponseType[T]>
  ) => void;
  hideModal: (data?: QuickActionResponseType[T]) => void;
  filteredQuickActions: typeof quickActions;
};
